import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperStats, useCommerceList, type AccountTypeKey } from '@/hooks/useSuperStats';
import { useAuth } from '@/hooks/useAuth';
import { downloadSuperStatsCsv } from '@/lib/statsExport';
import { generateInvestorReport } from '@/lib/investorReport';
import { buildZifekPresentation } from '@/lib/presentationExport';
import { createDocumentWithContent } from '@/lib/documents';
import KpiCard from './components/KpiCard';
import StatsFilterHero from './components/StatsFilterHero';
import TractionPanel from './components/TractionPanel';
import ContentsStats from './components/ContentsStats';
import AccountTypeBreakdown from './components/AccountTypeBreakdown';
import AcquisitionChart from './components/AcquisitionChart';
import VisitsChart from './components/VisitsChart';
import TrafficSources from './components/TrafficSources';
import SourceEvolution from './components/SourceEvolution';
import TopListCard from './components/TopListCard';
import RecentVisitors from './components/RecentVisitors';
import GeoBreakdown from './components/GeoBreakdown';

const FILTER_KEY = 'zifek_superstats_filter';

function isoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function num(value: number): string {
  return value.toLocaleString('fr-FR');
}

function currencySymbol(code: string): string {
  const map: Record<string, string> = {
    MAD: 'DH',
    EUR: '€',
    USD: '$',
    GBP: '£',
    XOF: 'CFA',
  };
  return map[code] || code;
}

function formatAmount(value: number, code: string): string {
  return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencySymbol(code)}`;
}

interface AppliedFilter {
  startDate: string;
  endDate: string;
  scopeId: number;
}

/** Restaure le dernier périmètre consulté (persistance locale). */
function loadSavedFilter(): AppliedFilter | null {
  try {
    const raw = localStorage.getItem(FILTER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AppliedFilter>;
    if (
      typeof parsed.startDate === 'string'
      && typeof parsed.endDate === 'string'
      && typeof parsed.scopeId === 'number'
    ) {
      return { startDate: parsed.startDate, endDate: parsed.endDate, scopeId: parsed.scopeId };
    }
  } catch {
    /* stockage indisponible */
  }
  return null;
}

function saveFilter(filter: AppliedFilter): void {
  try {
    localStorage.setItem(FILTER_KEY, JSON.stringify(filter));
  } catch {
    /* stockage indisponible */
  }
}

export default function SuperAdminStatsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return isoDate(d);
  }, []);

  const saved = useMemo(loadSavedFilter, []);
  const initialStart = saved?.startDate || defaultStart;
  const initialEnd = saved?.endDate || isoDate(today);
  const initialScope = saved?.scopeId ?? 0;

  const { commerces, loading: commercesLoading } = useCommerceList();

  const [startInput, setStartInput] = useState(initialStart);
  const [endInput, setEndInput] = useState(initialEnd);
  const [scopeInput, setScopeInput] = useState(initialScope);
  const [applied, setApplied] = useState<AppliedFilter>({
    startDate: initialStart,
    endDate: initialEnd,
    scopeId: initialScope,
  });
  const [exportingPptx, setExportingPptx] = useState(false);
  const [savedDocName, setSavedDocName] = useState<string | null>(null);
  const [accountTypeFilter, setAccountTypeFilter] = useState<AccountTypeKey | null>(null);
  // Filtre par source de trafic du classement des produits (Google / WhatsApp…).
  const [productSourceFilter, setProductSourceFilter] = useState<string | null>(null);
  // Filtre par source de trafic du classement des services (Google / WhatsApp…).
  const [serviceSourceFilter, setServiceSourceFilter] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 6000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const { stats, loading, error, retry } = useSuperStats(
    { ...applied, accountType: accountTypeFilter },
    commerces,
  );

  const handleApply = () => {
    let start = startInput || defaultStart;
    let end = endInput || isoDate(today);
    if (start > end) {
      [start, end] = [end, start];
      setStartInput(start);
      setEndInput(end);
    }
    const next: AppliedFilter = { startDate: start, endDate: end, scopeId: scopeInput };
    setApplied(next);
    saveFilter(next);
  };

  // Filtre de période rapide (7j / 30j / 90j / année) : recalcule et applique
  // immédiatement le périmètre en un seul clic.
  const handleQuickRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    const s = isoDate(start);
    const e = isoDate(end);
    setStartInput(s);
    setEndInput(e);
    const next: AppliedFilter = { startDate: s, endDate: e, scopeId: scopeInput };
    setApplied(next);
    saveFilter(next);
  };

  // Période rapide active (pour mettre en surbrillance le bon bouton).
  const activeQuickDays = useMemo(() => {
    const start = new Date(`${applied.startDate}T00:00:00`);
    const end = new Date(`${applied.endDate}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    const diff = Math.round((end.getTime() - start.getTime()) / 86400000);
    return [7, 30, 90, 365].includes(diff) ? diff : null;
  }, [applied]);

  const scopeLabel = applied.scopeId === 0
    ? 'Tout le SaaS'
    : commerces.find((c) => c.id === applied.scopeId)?.name || `Site #${applied.scopeId}`;

  const meta = {
    scopeLabel,
    startDate: applied.startDate,
    endDate: applied.endDate,
  };

  const handleExport = () => {
    downloadSuperStatsCsv(stats, { ...meta, scopeId: applied.scopeId });
  };

  const handleExportPdf = () => {
    generateInvestorReport(stats, meta);
  };

  const handleExportPptx = async () => {
    if (!user) {
      setToast({ type: 'error', text: 'Vous devez être connecté pour enregistrer la présentation.' });
      return;
    }
    setExportingPptx(true);
    try {
      const { fileName, content } = buildZifekPresentation(stats, meta);
      await createDocumentWithContent(user.id, fileName, 'powerpoint', content);
      setSavedDocName(fileName);
      setToast({
        type: 'success',
        text: `Présentation enregistrée dans Documents Zifek → « ${fileName} ». Ouvrez l'app Documents pour la consulter et l'exporter.`,
      });
    } catch {
      setToast({ type: 'error', text: "Échec de l'enregistrement de la présentation dans Documents." });
    } finally {
      setExportingPptx(false);
    }
  };

  const handleOpenDocuments = () => {
    navigate('/dashboard/documents');
  };

  // Filtre par type de compte : clique sur une catégorie (Boutique / Services / …).
  const handleAccountTypeClick = (key: string) => {
    const next = (accountTypeFilter === key ? null : key) as AccountTypeKey | null;
    setAccountTypeFilter(next);
  };

  const inProgress = loading || commercesLoading;
  const hasData = stats.visitsPeriod > 0 || stats.visitsAllTime > 0;
  const previousTotal = stats.comparisonByDay.reduce((sum, point) => sum + point.previous, 0);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-5 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-dashboard-3-line text-primary-600"></i>
            Statistiques &amp; vue globale
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Centre d&apos;analyse de la plateforme — périmètre : <strong>{scopeLabel}</strong>
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-xs font-medium text-foreground-600 self-start md:self-auto">
          <i className="ri-archive-2-line text-primary-500"></i>
          Périmètre mémorisé automatiquement
        </span>
      </div>

      <StatsFilterHero
        commerces={commerces}
        scopeId={scopeInput}
        startDate={startInput}
        endDate={endInput}
        loading={commercesLoading}
        onScopeChange={setScopeInput}
        onStartChange={setStartInput}
        onEndChange={setEndInput}
        onQuickRange={handleQuickRange}
        activeQuickDays={activeQuickDays}
        onApply={handleApply}
        onExport={handleExport}
        onExportPdf={handleExportPdf}
        onExportPptx={handleExportPptx}
        onOpenDocuments={handleOpenDocuments}
        savedDocName={savedDocName}
        exportDisabled={!hasData}
        exportingPptx={exportingPptx}
      />

      {error && (
        <div className="flex flex-col items-center justify-center py-5 mb-6 bg-background-50 border border-background-200/70 rounded-lg">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button
            onClick={retry}
            className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* ── Traction (investisseurs) ─────────────────────────── */}
      <TractionPanel
        traction={stats.traction}
        countries={stats.geoCountries}
        loading={inProgress}
      />

      {/* ── KPI depuis la création / période ─────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-3 md:mb-4">
        <KpiCard
          icon="ri-line-chart-line"
          label="Visites totales (depuis la création)"
          value={num(stats.visitsAllTime)}
          iconClass="bg-foreground-950 text-background-50"
        />
        <KpiCard
          icon="ri-user-follow-line"
          label="Visiteurs uniques (depuis la création)"
          value={num(stats.uniqueAllTime)}
          iconClass="bg-accent-100 text-accent-700"
        />
        <KpiCard
          icon="ri-eye-line"
          label="Visites (période analysée)"
          value={num(stats.visitsPeriod)}
          iconClass="bg-secondary-100 text-secondary-700"
          delta={stats.deltas.visits}
          sub="vs période précédente"
        />
        <KpiCard
          icon="ri-user-line"
          label="Visiteurs uniques (période analysée)"
          value={num(stats.uniquePeriod)}
          iconClass="bg-primary-100 text-primary-700"
          delta={stats.deltas.unique}
          sub="vs période précédente"
        />
      </div>

      {/* ── KPI par type de visite ───────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-3 md:mb-4">
        <KpiCard
          icon="ri-home-8-line"
          label="Visites de la page d'accueil Zifek"
          value={num(stats.landingVisits)}
          iconClass="bg-accent-100 text-accent-700"
        />
        <KpiCard
          icon="ri-global-line"
          label="Visites des sites de la plateforme"
          value={num(stats.siteVisits)}
          iconClass="bg-secondary-100 text-secondary-700"
        />
        <KpiCard
          icon="ri-shopping-bag-3-line"
          label="Visites des pages produits"
          value={num(stats.productVisits)}
          iconClass="bg-primary-100 text-primary-700"
        />
        <KpiCard
          icon="ri-briefcase-4-line"
          label="Visites des pages services"
          value={num(stats.serviceVisits)}
          iconClass="bg-accent-100 text-accent-700"
        />
      </div>

      {/* ── KPI des pages de contenu publiques ───────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-5 md:mb-6">
        <KpiCard
          icon="ri-calendar-check-line"
          label="Visites des pages de réservation"
          value={num(stats.bookingVisits)}
          iconClass="bg-accent-100 text-accent-700"
        />
        <KpiCard
          icon="ri-survey-line"
          label="Visites des formulaires"
          value={num(stats.formVisits)}
          iconClass="bg-secondary-100 text-secondary-700"
        />
        <KpiCard
          icon="ri-hotel-line"
          label="Visites des hébergements"
          value={num(stats.hebergementVisits)}
          iconClass="bg-primary-100 text-primary-700"
        />
        <KpiCard
          icon="ri-focus-3-line"
          label="Conversion fiche service → réservation"
          value={stats.serviceConversion == null ? '—' : `${stats.serviceConversion.toFixed(1)} %`}
          iconClass="bg-accent-100 text-accent-700"
          sub={`${num(stats.bookings)} réservation(s) / ${num(stats.serviceVisits)} vue(s)`}
        />
      </div>

      {/* ── Conversion fiche produit → commande (symétrique de service → réservation) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 md:gap-4 mb-5 md:mb-6">
        <KpiCard
          icon="ri-shopping-bag-3-line"
          label="Conversion fiche produit → commande"
          value={stats.productConversion == null ? '—' : `${stats.productConversion.toFixed(1)} %`}
          iconClass="bg-primary-100 text-primary-700"
          sub={`${num(stats.orders)} commande(s) / ${num(stats.productVisits)} vue(s)`}
        />
      </div>

      {/* ── KPI plateforme ───────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-5 md:mb-6">
        <KpiCard
          icon="ri-group-line"
          label="Comptes utilisateurs des sites"
          value={num(stats.siteUsers)}
          iconClass="bg-secondary-100 text-secondary-700"
        />
        <KpiCard
          icon="ri-store-2-line"
          label="Sites de la plateforme"
          value={num(stats.sites)}
          iconClass="bg-foreground-950 text-background-50"
        />
        <KpiCard
          icon="ri-shopping-cart-2-line"
          label="Commandes (période)"
          value={num(stats.orders)}
          iconClass="bg-accent-100 text-accent-700"
          delta={stats.deltas.orders}
          sub="vs période précédente"
        />
        <KpiCard
          icon="ri-money-dollar-circle-line"
          label="CA commandes (période)"
          value={formatAmount(stats.revenue, stats.currency)}
          iconClass="bg-secondary-100 text-secondary-700"
          delta={stats.deltas.revenue}
          sub="vs période précédente"
        />
      </div>

      {/* ── Contenus & publications ──────────────────────────── */}
      <ContentsStats data={stats.marketplace} loading={inProgress} />

      {/* ── Répartition par type de compte ───────────────────── */}
      <AccountTypeBreakdown
        items={stats.accountTypes}
        loading={inProgress}
        activeType={accountTypeFilter}
        onTypeClick={handleAccountTypeClick}
      />

      {accountTypeFilter && (
        <div className="flex items-center gap-2 flex-wrap mb-5 md:mb-6 px-3.5 py-2.5 rounded-lg bg-primary-50 border border-primary-200/60">
          <i className="ri-filter-3-line text-primary-600"></i>
          <span className="text-sm text-foreground-700">
            Page filtrée par type de compte : <strong>{accountTypeFilter}</strong>
          </span>
          <button
            type="button"
            onClick={() => setAccountTypeFilter(null)}
            className="ml-auto text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-close-line mr-0.5"></i>Réinitialiser
          </button>
        </div>
      )}

      {/* ── Acquisition : nouveaux sites / comptes ───────────── */}
      <AcquisitionChart data={stats.acquisition} loading={inProgress} />

      {/* ── Évolution + sources ──────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-5 md:mb-6">
        <div className="lg:col-span-2">
          <VisitsChart
            data={stats.comparisonByDay}
            total={stats.visitsPeriod}
            previousTotal={previousTotal}
            loading={inProgress}
          />
        </div>
        <TrafficSources items={stats.sources} loading={inProgress} />
      </div>

      {/* ── Évolution des sources dans le temps ─────────────── */}
      <SourceEvolution data={stats.sourceEvolution} loading={inProgress} />

      {/* ── Classements ──────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-5 md:mb-6">
        <TopListCard
          title="Sites les plus visités"
          icon="ri-global-line"
          iconClass="text-secondary-700"
          items={stats.topSites}
          loading={inProgress}
          emptyText="Aucun site visité sur cette période"
        />
        <TopListCard
          title="Produits les plus visités"
          icon="ri-shopping-bag-3-line"
          iconClass="text-primary-500"
          items={productSourceFilter ? (stats.topProductsBySource[productSourceFilter] || []) : stats.topProducts}
          loading={inProgress}
          emptyText={productSourceFilter
            ? `Aucun produit vu depuis « ${productSourceFilter} » sur cette période`
            : 'Aucun produit visité sur cette période'}
          filters={stats.productSources.slice(0, 5).map((s) => ({ key: s.name, label: s.name }))}
          activeFilter={productSourceFilter}
          onFilterChange={setProductSourceFilter}
        />
        <TopListCard
          title="Services les plus visités"
          icon="ri-briefcase-4-line"
          iconClass="text-accent-600"
          items={serviceSourceFilter ? (stats.topServicesBySource[serviceSourceFilter] || []) : stats.topServices}
          loading={inProgress}
          emptyText={serviceSourceFilter
            ? `Aucun service vu depuis « ${serviceSourceFilter} » sur cette période`
            : 'Aucun service visité sur cette période'}
          filters={stats.serviceSources.slice(0, 5).map((s) => ({ key: s.name, label: s.name }))}
          activeFilter={serviceSourceFilter}
          onFilterChange={setServiceSourceFilter}
        />
      </div>

      {/* ── Derniers visiteurs + géo ─────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <div className="lg:col-span-2">
          <RecentVisitors items={stats.recentVisits} loading={inProgress} />
        </div>
        <GeoBreakdown
          countries={stats.geoCountries}
          cities={stats.geoCities}
          total={stats.geoTotal}
          pending={stats.geoPending}
          loading={inProgress}
        />
      </div>

      <p className="text-[11px] text-foreground-400 mt-6 text-center">
        Source : Readdy Backend — données réelles de la plateforme.
        Géolocalisation par IP des visites publiques (pays / villes).
      </p>

      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] max-w-[90%] px-4 py-3 rounded-lg border text-sm font-medium flex items-start gap-2 ${
            toast.type === 'success'
              ? 'bg-accent-50 border-accent-200 text-accent-800'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          <i
            className={`mt-0.5 ${
              toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            }`}
          ></i>
          <span className="flex-1">{toast.text}</span>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="cursor-pointer text-foreground-400 hover:text-foreground-700"
            aria-label="Fermer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}
    </div>
  );
}